import { Moon, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { classNames } from '@/ui/utils/main'
import {
  setPublicThemeOverride,
  LIGHT_THEME_ID,
  subscribeToPublicThemeOverride,
  type PublicThemeOverrideMode,
} from '@/themes/publicThemeOverride'
import { useCurrentTheme } from '@/application/hooks/useCurrentTheme'

/**
 * Light/dark toggle (Navbar, between the language selector and the
 * auth/avatar area), shown to visitors and authenticated users alike. Purely a client-side per-device preference: writes to
 * localStorage via `setPublicThemeOverride`, never touches the backend
 * `publicTheme` setting.
 *
 * Reflects the CURRENTLY EFFECTIVE theme (via `useCurrentTheme`) rather than
 * only the stored override, so a first-time visitor (no override yet) still
 * sees the toggle correctly pre-set to whatever `publicTheme` resolved to.
 */
export function PublicThemeToggle() {
  const { t } = useTranslation()
  const theme = useCurrentTheme()
  const [, forceUpdate] = useState(0)

  // Re-render on a same-tab or cross-tab override change so the pressed
  // state stays in sync even if changed from another tab/toggle instance.
  useEffect(() => subscribeToPublicThemeOverride(() => forceUpdate((tick) => tick + 1)), [])

  const isLight = theme.id === LIGHT_THEME_ID

  const handleToggle = () => {
    const next: PublicThemeOverrideMode = isLight ? 'dark' : 'light'
    setPublicThemeOverride(next)
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      aria-label={isLight ? t('nav.theme_toggle_to_dark') : t('nav.theme_toggle_to_light')}
      aria-pressed={isLight}
      title={isLight ? t('nav.theme_toggle_to_dark') : t('nav.theme_toggle_to_light')}
      className={classNames(
        'flex items-center justify-center h-7 w-7 rounded border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors',
      )}
    >
      {isLight ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />}
    </button>
  )
}
